import { useMutation } from '@apollo/client'
import { Formik, Form, FormikHelpers } from 'formik'
import { useRef, useState } from 'react'
import { toast } from 'react-toastify'

import apolloClient from '../../graphql/apolloClient'
import { GENERATE_SITEKEY } from '../../graphql/sitekeys'
import { ImagePropsForDropZone } from '../../types/image'
import {
  AssociatedGateway,
  BookingRedirect,
  SiteKeyUpload
} from '../../types/sitekey'
import SitekeyBasicInfo from './SitekeyBasicInfo'
import SitekeyFooter from './SitekeyFooter'
import SitekeyLogoUpload from './SitekeyLogoUpload'
import SitekeyWBSDetails from './SitekeyWBSDetails'
import { SiteKeyItemsValidationSchema } from './validations/SiteKeyItems'

// get the sitekey single one data
const SITEKEY_FORM_INIT_VALUES: SiteKeyUpload = {
  EntityType: '',
  EntityId: '',
  UserId: '',
  Email: '',
  AssociatedGateways: [],
  Type: 'Widget',
  CORSURLs: [],
  Hosts: [],
  Logo: {
    EN: undefined,
    FA: undefined
  },
  Favicon: {
    EN: undefined,
    FA: undefined
  },
  BookingRedirects: [],
  EntityCode: '',
  DisplayEntityCode: '',
  WalletType: 'ProviderCredit',
  Mode: 'single'
}

const SitekeyForm = () => {
  const formikValuesRef = useRef(null)
  const [bookingRedirectsState, setBookingRedirectsState] = useState<
    BookingRedirect[]
  >([])
  const [associatedGateways, setAssociatedGateways] = useState<
    AssociatedGateway[]
  >([])

  const [mutateFunctionForCreate, { loading: loadingForGenerateSitekey }] =
    useMutation(GENERATE_SITEKEY, { notifyOnNetworkStatusChange: true })

  const stripImageDisplayFields = (
    image?: ImagePropsForDropZone
  ): ImagePropsForDropZone | undefined => {
    if (!image) return image

    const { displayEntityCode, ...rest } = image
    return rest
  }
  const onSave = (
    values: SiteKeyUpload,
    { setSubmitting }: FormikHelpers<SiteKeyUpload>
  ) => {
    const { SiteKey, DisplayEntityCode, Email, Logo, Favicon, ...rest } = values

    const payload = {
      ...rest,
      BookingRedirects: values.BookingRedirects, // untouched ✅
      AssociatedGateways: associatedGateways,
      Logo: {
        EN: stripImageDisplayFields(Logo.EN),
        FA: stripImageDisplayFields(Logo.FA)
      },
      Favicon: {
        EN: stripImageDisplayFields(Favicon.EN),
        FA: stripImageDisplayFields(Favicon.FA)
      }
    }

    mutateFunctionForCreate({
      variables: {
        input: payload
      }
    })
      .then(({ data: response }) => {
        toast.success(response.addNewSiteKey.message)
      })
      .catch((error) => {
        if (error instanceof Error) {
          toast.error(error.message)
        } else {
          toast.error('ERROR!')
        }
      })
      .finally(() => {
        setSubmitting(false)
      })

    apolloClient.cache.evict({
      fieldName: 'sitekeys'
    })
  }

  const [areAllLogosUploaded, setAreAllLogosUploaded] = useState<boolean>(false)

  return (
    <Formik
      innerRef={formikValuesRef}
      initialValues={SITEKEY_FORM_INIT_VALUES}
      onSubmit={onSave}
      enableReinitialize
      validationSchema={SiteKeyItemsValidationSchema}>
      {({
        isSubmitting,
        values,
        setFieldValue,
        errors,
        touched,
        handleBlur,
        isValid
      }) => {
        if (
          values.Logo.EN?.fileURL &&
          values.Logo.FA?.fileURL &&
          values.Favicon.EN?.fileURL &&
          values.Favicon.FA?.fileURL
        ) {
          setAreAllLogosUploaded(true)
        }

        return (
          <Form>
            <div>
              <SitekeyBasicInfo
                {...{
                  values,
                  setFieldValue,
                  errors,
                  touched,
                  handleBlur,
                  isSubmitting,
                  bookingRedirectsState,
                  setBookingRedirectsState
                }}
              />
              <SitekeyWBSDetails
                {...{
                  values,
                  setFieldValue,
                  errors,
                  touched,
                  handleBlur,
                  isSubmitting,
                  associatedGateways,
                  setAssociatedGateways
                }}
              />
              <SitekeyLogoUpload
                {...{
                  values,
                  setFieldValue
                }}
              />
              <SitekeyFooter
                areAllLogosUploaded={areAllLogosUploaded}
                {...{
                  isValid,
                  isSubmitting,
                  loadingForGenerateSitekey
                }}
              />
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}
export default SitekeyForm
